import {isEmpty} from '@shared/helpers/lang'

import {
    PromotionCardCodeType,
    PromotionCardProductNameCodeType,
    PromotionStatus,
    PromotionStatusType,
    PromotionType,
    PromotionUnApplicableReason,
} from '@web/core/constants'
import {CardState, GroupItem, isCardProductPromotion} from '@web/slices/card/slice'

import {FetchIntegratedDiscountResponse} from '../interfaces'

type CardPromotion = FetchIntegratedDiscountResponse['cardPromotionV2']
type GroupedCardPromotion = NonNullable<CardPromotion>['groupedCardPromotions'][number]
type FailedCardPromotion = NonNullable<CardPromotion>['failedCardPromotions'][number]

/**
 * 최초 진입 시 case
 * - 나할 조회 실패한 경우  -> 영역 미노출
 * - 프로모션 없는 경우 -> 적용 가능한 할인이 없습니다.
 * - 나할 정책이 있는 경우 -> 적용가능 최대할인 있어요
 * - 나할 정책 없는 경우 -> 카드 할인을 선택해주세요
 */
export const getPromotionStatus = ({
    groupedCardPromotions,
}: {
    /** 카드 프로모션 정책 */
    groupedCardPromotions: GroupedCardPromotion[]
}): PromotionStatusType => {
    if (isEmpty(groupedCardPromotions)) {
        return PromotionStatus.NO_AVAILABLE_PROMOTION
    }
    if (groupedCardPromotions.some(({myOptimalDiscountPayAmount}) => myOptimalDiscountPayAmount)) {
        return PromotionStatus.HAS_MY_OPTIMIZED_PROMOTION
    }
    return PromotionStatus.AVAILABLE
}

export const convertCardPromotions = ({
    group,
    failed,
}: {
    group: GroupedCardPromotion
    failed: FailedCardPromotion[]
}): GroupItem => {
    return {
        card: {
            companyCode: group.cardCompanyCode as PromotionCardCodeType,
            companyName: group.cardCompanyName,
            detailInfos: group.cardDetails
                ? group.cardDetails.map((detailInfo) => {
                      return {
                          companyId: detailInfo.cardCompanyId,
                          issuerCode: detailInfo.cardIssuerCode ?? undefined,
                          issuerName: detailInfo.cardIssuerName ?? undefined,
                          types: detailInfo.cardTypes,
                          ownerTypes: detailInfo.cardOwnerTypes,
                          productCodes:
                              detailInfo.cardProductCodes && !isEmpty(detailInfo.cardProductCodes)
                                  ? detailInfo.cardProductCodes
                                  : undefined,
                      }
                  })
                : undefined,
            productNameCode: (group.cardProductNameCode as PromotionCardProductNameCodeType) ?? undefined,
            productName: group.cardProductName ?? undefined,
        },
        totalPromotionApplyAmount: group.totalPromotionApplyAmount,
        cardPromotions: group.cardPromotions.map((promotionInfo) => {
            return {
                policyNo: promotionInfo.promotionPolicyNo,
                accumulationPolicySequence: promotionInfo.accumulationPolicySequence,
                name: promotionInfo.promotionName,
                applyType: promotionInfo.promotionApplyType,
                applyValue: promotionInfo.promotionApplyValue,
                minPayAmount: promotionInfo.promotionMinPayAmount,
                maxApplyAmount: promotionInfo.promotionMaxApplyAmount ?? undefined,
                startDateTime: promotionInfo.startDateTime,
                endDateTime: promotionInfo.endDateTime,
                cautionText: promotionInfo.cautionText ?? undefined,
                isRestrictedByMembership: promotionInfo.restrictedByMembership,
                duplicateParticipation: promotionInfo.duplicateParticipation
                    ? {
                          restrictionType: promotionInfo.duplicateParticipation.restrictionType,
                          maxOverlapCount: promotionInfo.duplicateParticipation.restrictionCount,
                          applicableRemainCount: promotionInfo.duplicateParticipation.applicableRemainCount,
                      }
                    : undefined,
                applicableProducts: promotionInfo.applicableProducts
                    ? promotionInfo.applicableProducts.map((productInfo) => {
                          return {
                              id: productInfo.orderSheetProductId,
                              itemId: productInfo.orderSheetItemId,
                              merchantId: productInfo.merchantProductId,
                              name: productInfo.productName,
                              applyAmount: productInfo.promotionApplyAmount,
                          }
                      })
                    : undefined,
                burdenInfos: promotionInfo.burdenInfos?.map((burdenInfo) => {
                    return {
                        promotionBurdenOwnerType: burdenInfo.promotionBurdenOwnerType,
                        burdenRate: burdenInfo.burdenRate,
                    }
                }),
                burdenBillingType: promotionInfo.burdenBillingType ?? undefined,
                affiliateCard: promotionInfo.affiliateCard,
                productName: promotionInfo.cardProductName ?? undefined,
                productNameCode: promotionInfo.cardProductNameCode ?? undefined,
                productCodes: promotionInfo.cardProductCodes ?? undefined,
            }
        }),
        unApplicableReason: (function () {
            if (
                !!failed &&
                failed.some(
                    ({cardCompanyCode, cardProductNameCode}) =>
                        cardCompanyCode === group.cardCompanyCode && cardProductNameCode === group.cardProductNameCode,
                )
            ) {
                return PromotionUnApplicableReason.TOO_MANY_PROMOTION
            } else if (
                group.cardPromotions &&
                group.cardPromotions.every((promotion) => isEmpty(promotion.applicableProducts))
            ) {
                return PromotionUnApplicableReason.MINIMUM_PRODUCT_PRICE
            }
            return undefined
        })(),
        type: group.cardProductNameCode ? PromotionType.CARD_PRODUCT : PromotionType.GENERAL,
        myOptimalDiscountPayAmount: group.myOptimalDiscountPayAmount ?? undefined,
    }
}

export const getGroupedCardPromotions = ({
    groups,
    failed,
}: {
    groups: GroupedCardPromotion[]
    failed: FailedCardPromotion[]
}): CardState['promotion']['groups'] => {
    return groups
        .filter((group) => {
            const {cardProductNameCode} = group
            /** 일반 프로모션 이거나 카드상품 프로모션인데 FE에서 관리하고 있는 카드상품이어야한다. */
            return !cardProductNameCode || isCardProductPromotion(cardProductNameCode)
        })
        .map((group) => convertCardPromotions({group, failed}))
}

export const getMyOptimalDiscountPromotion = (
    groups: CardState['promotion']['groups'],
): ItemType<CardState['promotion']['groups']> | undefined => {
    return groups.find((group) => group.myOptimalDiscountPayAmount)
}
